import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  PREMIUM_PLAN_NAME,
  PREMIUM_PRICE_LABEL,
  STRIPE_CHECKOUT_URL,
  getTierConfig,
  isPremiumTier
} from '../config/entitlements';
import { resolveTierFromUser } from '../utils/runtimeTier';
import './UpgradePage.css';

const FREE_FEATURES = [
  'Full combat table with initiative, HP, conditions, and Fog of War',
  'Shared table state for players and the Game Master',
  '1 saved battle that fades after 30 days',
  '2 custom heroes or NPCs and 2 custom monsters',
  '10 uploaded maps or token images'
];

const PREMIUM_FEATURES = [
  'Unlimited saved battles with no fade timer',
  'Unlimited custom heroes, NPCs, and monsters',
  'Encounter presets for reusable ambushes and boss fights',
  'House Rule Forge for custom crits, rests, and table rulings',
  '1 GB map and token vault for a full campaign library'
];

const UpgradePage = () => {
  const { user } = useUser();
  const tier = resolveTierFromUser(user);
  const tierConfig = getTierConfig(tier);
  const isPremium = isPremiumTier(tier);

  return (
    <div className="upgrade-page">
      <div className="upgrade-shell">
        <section className="upgrade-hero">
          <span className="upgrade-kicker">{isPremium ? 'Active Loadout' : 'Current Loadout'}</span>
          <h1>Choose Your Table Loadout</h1>
          <p>
            Free gets the party into the fight fast. {PREMIUM_PLAN_NAME} is for long-running campaigns, deeper prep,
            and a vault that does not run out of room.
          </p>
          <div className="upgrade-hero-actions">
            <span className="upgrade-current-plan">
              {isPremium ? `${PREMIUM_PLAN_NAME} is active.` : `${tierConfig.planName} is active.`}
            </span>
            <Link to="/" className="upgrade-button upgrade-button--ghost">
              Back to Dashboard
            </Link>
            {isPremium && (
              <Link to="/ruleset" className="upgrade-button upgrade-button--primary">
                Open House Rule Forge
              </Link>
            )}
          </div>
        </section>

        {!isPremium && (
          <section className="upgrade-note-card">
            <strong>When your account steps into {PREMIUM_PLAN_NAME}, every greyed-out control across the app wakes up instantly.</strong>
            <p>
              That includes endless battle saves, deeper prep tools, custom rules, and a much larger vault for maps and
              tokens.
            </p>
          </section>
        )}

        <section className="upgrade-grid">
          <article className={`upgrade-card${!isPremium ? ' is-current' : ''}`}>
            <div className="upgrade-card-header">
              <div>
                <span className="upgrade-plan-tag">Free</span>
                <h2>Start Playing Immediately</h2>
              </div>
              {!isPremium && <span className="upgrade-current-badge">Current</span>}
            </div>
            <p className="upgrade-plan-copy">
              Core combat is fully live, with just enough room for a one-shot or a small side quest.
            </p>
            <ul className="upgrade-list">
              {FREE_FEATURES.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </article>

          <article className={`upgrade-card upgrade-card--premium${isPremium ? ' is-current' : ''}`}>
            <div className="upgrade-card-header">
              <div>
                <span className="upgrade-plan-tag upgrade-plan-tag--premium">{PREMIUM_PLAN_NAME}</span>
                <h2>{PREMIUM_PRICE_LABEL}</h2>
              </div>
              {isPremium && <span className="upgrade-current-badge">Active</span>}
            </div>
            <p className="upgrade-plan-copy">
              Build, save, and run the whole campaign without rails.
            </p>
            <ul className="upgrade-list">
              {PREMIUM_FEATURES.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            {!isPremium && (
              <a
                href={STRIPE_CHECKOUT_URL}
                className="upgrade-button upgrade-button--primary upgrade-checkout-cta"
                target="_blank"
                rel="noopener noreferrer"
              >
                Upgrade to {PREMIUM_PLAN_NAME}
              </a>
            )}
          </article>
        </section>
      </div>
    </div>
  );
};

export default UpgradePage;
